import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';
import LocalizedMessage from '../common/LocalizedMessage';
import PermissionsService from "../../services/PermissionsService";
import TeacherExtensionsFields from './TeacherExtensionsFields';
import { EMPTY_TEACHER_EXTENSIONS } from '../../services/TeacherAccessService';
import CreateAccountModal from "./CreateAccountModal";
import { localizeSystemText } from '../../i18n/messages';

const ACCESS_LABELS = {
  "": "Brak",
  TEACHER: "Nauczyciel",
  OPERATOR: "Operator",
  ADMIN: "Administrator",
};

const emptyFilters = {
  text: "",
  accessLevel: "all",
  clientLink: "all",
  teacherLink: "all",
};

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function matchesLinkFilter(value, filter) {
  if (filter === "all") return true;
  if (filter === "linked") return Boolean(value);
  if (filter === "unlinked") return !value;
  return true;
}

function filterAccounts(accounts, filters) {
  const text = normalizeText(filters.text);
  return accounts
    .filter(account => !text || [
      account.email,
      account.firstName,
      account.lastName,
      account.displayName,
      account.clientDisplayName,
      account.teacherDisplayName,
    ].some(value => normalizeText(value).includes(text)))
    .filter(account => filters.accessLevel === "all" || (account.accessLevel || "") === filters.accessLevel)
    .filter(account => matchesLinkFilter(account.clientId, filters.clientLink))
    .filter(account => matchesLinkFilter(account.teacherId, filters.teacherLink));
}

function Permissions() {
  const { t: tr } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [accountCandidates, setAccountCandidates] = useState({ clients: [], teachers: [] });
  const [accessLevels, setAccessLevels] = useState(["", "TEACHER", "OPERATOR", "ADMIN"]);
  const [drafts, setDrafts] = useState({});
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [rowState, setRowState] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createError, setCreateError] = useState("");
  const [extensionsEditor, setExtensionsEditor] = useState(null);

  const applyPermissionsPayload = (payload) => {
    setAccounts(payload.accounts || []);
    setClientOptions(payload.clientOptions || []);
    setTeacherOptions(payload.teacherOptions || []);
    setAccountCandidates(payload.accountCandidates || { clients: [], teachers: [] });
    setAccessLevels(payload.accessLevels || ["", "TEACHER", "OPERATOR", "ADMIN"]);
    setLoadError("");
  };

  const loadPermissions = (showLoading) => {
    if (showLoading) setLoading(true);
    return PermissionsService.getAccountPermissions()
      .then(applyPermissionsPayload)
      .catch(error => setLoadError(error.message))
      .finally(() => {
        if (showLoading) setLoading(false);
      });
  };

  useEffect(() => {
    loadPermissions(true);
  }, []);

  const visibleAccounts = useMemo(() => filterAccounts(accounts, filters), [accounts, filters]);

  const getDraft = account => drafts[account.accountId] || {
    accessLevel: account.accessLevel || "",
    clientId: account.clientId || "",
    teacherId: account.teacherId || "",
    teacherExtensions: account.teacherExtensions || { ...EMPTY_TEACHER_EXTENSIONS },
    auditRead: account.auditRead === true,
    expectedPermissionsVersion: account.permissionsVersion || 0,
  };

  const updateDraft = (account, field, value) => {
    setDrafts(current => ({
      ...current,
      [account.accountId]: {
        ...getDraft(account),
        [field]: value,
        ...(field === 'accessLevel' && value !== 'OPERATOR' ? { auditRead: false } : {}),
        ...(field === 'teacherId' && value !== getDraft(account).teacherId ? { teacherExtensions: { ...EMPTY_TEACHER_EXTENSIONS } } : {}),
      },
    }));
    setRowState(current => ({
      ...current,
      [account.accountId]: { status: "", error: "" },
    }));
  };

  const save = (account, teacherExtensions) => {
    const draft = { ...getDraft(account), ...(teacherExtensions ? { teacherExtensions } : {}) };
    if (draft.accessLevel === "TEACHER" && !draft.teacherId) {
      setRowState(current => ({
        ...current,
        [account.accountId]: {
          status: "",
          error: tr("manage:teacherAccessRequiresALinkedTeacherProfile_266"),
        },
      }));
      return Promise.resolve();
    }

    setRowState(current => ({
      ...current,
      [account.accountId]: { status: "saving", error: "" },
    }));

    const payload = { ...draft };
    if (draft.accessLevel !== 'TEACHER' || !draft.teacherId) delete payload.teacherExtensions;
    return PermissionsService.updateAccountPermissions(account.accountId, payload)
      .then((updated) => {
        setAccounts(current => current.map(item => (
          item.accountId === account.accountId ? updated : item
        )));
        setDrafts((current) => {
          const next = { ...current };
          delete next[account.accountId];
          return next;
        });
        setRowState(current => ({
          ...current,
          [account.accountId]: { status: "saved", error: "" },
        }));
        if (teacherExtensions) setExtensionsEditor(null);
      })
      .catch(async (error) => {
        if (error.code === 409) {
          setExtensionsEditor(null);
          await loadPermissions(false);
          setDrafts((current) => {
            const next = { ...current };
            delete next[account.accountId];
            return next;
          });
        }
        setRowState(current => ({
          ...current,
          [account.accountId]: { status: "", error: error.code === 409 ? tr('manage:teacherExtensions.conflict') : error.message },
        }));
      });
  };

  const openCreateModal = () => {
    setCreateError("");
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateError("");
    setCreateModalOpen(false);
  };

  const createAccount = payload => PermissionsService.createAccountFromProfile(payload)
    .then(() => PermissionsService.getAccountPermissions())
    .then((permissionsPayload) => {
      applyPermissionsPayload(permissionsPayload);
      closeCreateModal();
    })
    .catch(error => setCreateError(error.message));

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="manage-title mb-0">{tr("manage:userPermissions_267")}</h2>
        {!loading && !loadError && (
          <button type="button" className="btn manage-button manage-red-button" onClick={openCreateModal}>{tr("manage:createAccount_167")}</button>
        )}
      </div>
      {loading && <div role="status">{tr("manage:loadingPermissions_268")}</div>}
      {loadError && <div className="alert alert-danger" role="alert"><LocalizedMessage isError value={loadError} /></div>}
      {!loading && !loadError && (
        <>
          <div className="row mx-0 mb-3">
            <div className="col-md-3 px-1">
              <label htmlFor="permissions-search">{tr("manage:search_169")}<input
                  id="permissions-search"
                  className="form-control"
                  value={filters.text}
                  onChange={event => setFilters({ ...filters, text: event.target.value })}
                />
              </label>
            </div>
            <div className="col-md-3 px-1">
              <label htmlFor="permissions-access-filter">{tr("manage:accessLevel_269")}<select
                  id="permissions-access-filter"
                  className="form-control"
                  value={filters.accessLevel}
                  onChange={event => setFilters({ ...filters, accessLevel: event.target.value })}
                >
                  <option value="all">{tr("manage:all_270")}</option>
                  {accessLevels.map(level => (
                    <option key={level || "none"} value={level}>{localizeSystemText(ACCESS_LABELS[level] || level, tr)}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="col-md-3 px-1">
              <label htmlFor="permissions-client-filter">{tr("manage:clientLink_271")}<select
                  id="permissions-client-filter"
                  className="form-control"
                  value={filters.clientLink}
                  onChange={event => setFilters({ ...filters, clientLink: event.target.value })}
                >
                  <option value="all">{tr("manage:all_270")}</option>
                  <option value="linked">{tr("manage:linked_272")}</option>
                  <option value="unlinked">{tr("manage:noClient_273")}</option>
                </select>
              </label>
            </div>
            <div className="col-md-3 px-1">
              <label htmlFor="permissions-teacher-filter">{tr("manage:teacherLink_274")}<select
                  id="permissions-teacher-filter"
                  className="form-control"
                  value={filters.teacherLink}
                  onChange={event => setFilters({ ...filters, teacherLink: event.target.value })}
                >
                  <option value="all">{tr("manage:all_270")}</option>
                  <option value="linked">{tr("manage:linked_272")}</option>
                  <option value="unlinked">{tr("manage:noTeacher_275")}</option>
                </select>
              </label>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm manage-text">
              <thead>
                <tr>
                  <th>{tr("manage:account_276")}</th>
                  <th>{tr("manage:client_108")}</th>
                  <th>{tr("manage:teacher_226")}</th>
                  <th>{tr("manage:access_277")}</th>
                  <th>Dziennik zmian</th>
                  <th>{tr("manage:action_278")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleAccounts.map((account) => {
                  const draft = getDraft(account);
                  const state = rowState[account.accountId] || {};
                  const hasExtensions = Object.keys(EMPTY_TEACHER_EXTENSIONS).some(key => (
                    (draft.teacherExtensions || EMPTY_TEACHER_EXTENSIONS)[key] !== EMPTY_TEACHER_EXTENSIONS[key]
                  ));
                  const extensionsLabel = tr(hasExtensions ? 'manage:teacherExtensions.configureActive' : 'manage:teacherExtensions.configure');
                  return (
                    <tr key={account.accountId} data-testid={`permissions-row-${account.accountId}`}>
                      <td>
                        <div>{account.email}</div>
                        <small>{account.displayName}</small>
                      </td>
                      <td>
                        <select
                          id={`client-${account.accountId}`}
                          aria-label={tr("manage:client_108")}
                          className="form-control"
                          value={draft.clientId}
                          onChange={event => updateDraft(account, "clientId", event.target.value)}
                        >
                          <option value="">{tr("manage:none_279")}</option>
                          {clientOptions.map(option => (
                            <option key={option.id} value={option.id}>{option.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          id={`teacher-${account.accountId}`}
                          aria-label={tr("manage:teacher_226")}
                          className="form-control"
                          value={draft.teacherId}
                          onChange={event => updateDraft(account, "teacherId", event.target.value)}
                        >
                          <option value="">{tr("manage:none_279")}</option>
                          {teacherOptions.map(option => (
                            <option key={option.id} value={option.id}>{option.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="d-flex flex-nowrap align-items-center">
                          <select
                            id={`access-${account.accountId}`}
                            aria-label={tr("manage:access_277")}
                            className="form-control"
                            value={draft.accessLevel}
                            onChange={event => updateDraft(account, "accessLevel", event.target.value)}
                          >
                            {accessLevels.map(level => (
                              <option key={level || "none"} value={level}>{localizeSystemText(ACCESS_LABELS[level] || level, tr)}</option>
                            ))}
                          </select>
                          {draft.accessLevel === 'TEACHER' && draft.teacherId && (
                          <button
                            type="button"
                            className={`btn ml-2 flex-shrink-0 ${hasExtensions ? 'manage-teal-button' : 'btn-light text-muted'}`}
                            title={extensionsLabel}
                            aria-label={extensionsLabel}
                            aria-haspopup="dialog"
                            disabled={state.status === 'saving'}
                            onClick={() => setExtensionsEditor({ account, value: { ...EMPTY_TEACHER_EXTENSIONS, ...draft.teacherExtensions } })}
                          >
                            <i className={`fas ${hasExtensions ? 'fa-user-shield' : 'fa-sliders-h'}`} aria-hidden="true" />
                          </button>
                          )}
                        </div>
                      </td>
                      <td>
                        <label htmlFor={`audit-access-${account.accountId}`} className="mb-0">
                          <input
                            id={`audit-access-${account.accountId}`}
                            type="checkbox"
                            checked={draft.auditRead}
                            disabled={draft.accessLevel !== 'OPERATOR' || state.status === 'saving'}
                            onChange={event => updateDraft(account, 'auditRead', event.target.checked)}
                          /> Dostęp do dziennika
                        </label>
                      </td>
                      <td className="text-nowrap">
                        <button
                          type="button"
                          className="btn manage-button manage-red-button"
                          disabled={state.status === "saving"}
                          onClick={() => save(account)}
                        >{tr("common:save")}
                        </button>
                        {state.status === "saved" && (
                          <span className="text-success ml-2" role="status" title={tr("manage:saved_280")}>
                            <i className="fas fa-check" aria-hidden="true" /><span className="sr-only">{tr("manage:saved_280")}</span>
                          </span>
                        )}
                        {state.error && <div className="text-danger" role="alert">{state.error}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {createModalOpen && (
        <CreateAccountModal
          accountCandidates={accountCandidates}
          error={createError}
          onClose={closeCreateModal}
          onCreate={createAccount}
        />
      )}
      {extensionsEditor && (
        <Modal
          show
          centered
          aria-labelledby="teacher-extensions-title"
          onHide={() => { if ((rowState[extensionsEditor.account.accountId] || {}).status !== 'saving') setExtensionsEditor(null); }}
          onEntered={node => {
            node.removeAttribute('aria-hidden');
            node.setAttribute('aria-labelledby', 'teacher-extensions-title');
          }}
        >
          <Modal.Header>
            <Modal.Title id="teacher-extensions-title">{tr('manage:teacherExtensions.title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-2">{extensionsEditor.account.email}</p>
            <TeacherExtensionsFields
              idPrefix="teacher-extensions-modal"
              value={extensionsEditor.value}
              disabled={(rowState[extensionsEditor.account.accountId] || {}).status === 'saving'}
              onChange={value => setExtensionsEditor(current => ({ ...current, value }))}
            />
            {(rowState[extensionsEditor.account.accountId] || {}).error && (
              <div role="alert" className="alert alert-danger mt-3">{rowState[extensionsEditor.account.accountId].error}</div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn manage-button manage-teal-button"
              disabled={(rowState[extensionsEditor.account.accountId] || {}).status === 'saving'}
              onClick={() => setExtensionsEditor(null)}
            >{tr('common:cancel')}
            </button>
            <button
              type="button"
              className="btn manage-button manage-red-button"
              disabled={(rowState[extensionsEditor.account.accountId] || {}).status === 'saving'}
              onClick={() => save(extensionsEditor.account, extensionsEditor.value)}
            >{tr('common:save')}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default Permissions;
